import { useFxWorkspaceContext } from '@mobile-mapping/providers';
import { t } from '@trimble/i18n';
import { createEffect, createMemo, createSignal } from 'solid-js';

import { type FxReferencesFilterMode } from '../selections/tree/filter-fx-reference-nodes';
import type { FxReferencesViewMode } from '../selections/tree/flatten-fx-reference-nodes';
import { useActiveFxWorkspaceTree } from '../selections/tree/useActiveFxWorkspaceTree';
import type { WorkspacesTabState } from './useWorkspacesTab.types';
import { useWorkspacesTabDialogs } from './useWorkspacesTabDialogs';

export function useWorkspacesTab(): WorkspacesTabState {
  const fxWorkspace = useFxWorkspaceContext();
  const [referencesFilterMode, setReferencesFilterMode] =
    createSignal<FxReferencesFilterMode>('workspace');
  const [referencesViewMode, setReferencesViewMode] =
    createSignal<FxReferencesViewMode>('tree');
  const { activeWorkspace, nodes, childrenLoadStatus } =
    useActiveFxWorkspaceTree({
      referencesFilterMode,
      referencesViewMode,
    });

  const dialogs = useWorkspacesTabDialogs(activeWorkspace);

  createEffect(() => {
    if (fxWorkspace.loadStatus() !== 'success') {
      return;
    }

    const workspaces = fxWorkspace.workspaces();
    if (workspaces.length === 0) {
      return;
    }

    if (fxWorkspace.activeWorkspaceId() === undefined) {
      fxWorkspace.activateWorkspace(workspaces[0].id);
    }
  });

  const hasWorkspaces = createMemo(() => fxWorkspace.workspaces().length > 0);

  const isError = createMemo(() => childrenLoadStatus() === 'error');

  const isLoading = createMemo(() => {
    if (isError()) {
      return false;
    }

    if (childrenLoadStatus() === 'pending') {
      return true;
    }

    // First-workspace activation runs in an effect after the list succeeds.
    // Until it commits, keep the spinner so we don't flash the empty state.
    return hasWorkspaces() && activeWorkspace() === undefined;
  });

  const hasWorkspaceReferences = createMemo(() => {
    const workspaceId = fxWorkspace.activeWorkspaceId();
    return (
      workspaceId !== undefined &&
      fxWorkspace.workspaceReferenceCount(workspaceId) > 0
    );
  });

  const hasActiveWorkspace = createMemo(() => activeWorkspace() !== undefined);

  const workspaceOptions = createMemo(() =>
    fxWorkspace.workspaces().map((workspace) => ({
      key: workspace.id,
      label: workspace.name,
    }))
  );

  const handleActiveWorkspaceChange = (workspaceId: string): void => {
    fxWorkspace.activateWorkspace(workspaceId);
  };

  const handleReferencesFilterChange = (
    value: FxReferencesFilterMode | null
  ): void => {
    if (value === 'active' || value === 'workspace') {
      setReferencesFilterMode(value);
    }
  };

  const handleReferencesViewToggle = (): void => {
    setReferencesViewMode((mode) => (mode === 'tree' ? 'flat' : 'tree'));
  };

  const referencesViewToggleIcon = createMemo(() =>
    referencesViewMode() === 'tree'
      ? 'tc-icon-view-list'
      : 'tc-icon-folder-tree'
  );

  const referencesViewToggleTitle = createMemo(() =>
    referencesViewMode() === 'tree'
      ? t('mobileMapping.fxWorkspace.references.viewFlat')
      : t('mobileMapping.fxWorkspace.references.viewTree')
  );

  const enterWorkspaceDataEditing = (): void => {
    fxWorkspace.enterWorkspaceDataEditing();
  };

  return {
    ...dialogs,
    referencesFilterMode,
    referencesViewToggleIcon,
    referencesViewToggleTitle,
    hasWorkspaces,
    isError,
    isLoading,
    hasWorkspaceReferences,
    hasActiveWorkspace,
    workspaceOptions,
    activeWorkspace,
    nodes,
    childrenLoadStatus,
    handleActiveWorkspaceChange,
    handleReferencesFilterChange,
    handleReferencesViewToggle,
    enterWorkspaceDataEditing,
  };
}
